import type { ObjectId } from 'mongodb'
import * as MsftGraph from '@microsoft/microsoft-graph-types'

export type Option<T> = T | undefined
export type Nullable<T> = T | null

export type Id = string

interface Identified {
  id: Id
}

interface DBIdentified {
  _id: ObjectId
}

export enum BulkMessageTaskStatus {
  Pending = 'pending',
  InProgress = 'in_progress',
  Completed = 'completed',
  Failed = 'failed'
}

type UserStatus = 'deleted'

interface UserData {
  name: string
  email: string
  emailVerified: Nullable<Date>
  status?: UserStatus
  statusUpdatedAt?: number
  onboardingNudgeSentAt?: Date
  firstMessageCongratsSentAt?: Date
  templatesOfferSentAt?: Date
  // User preferences
  engagementOptOut?: boolean
}

export interface UserDBO extends DBIdentified, UserData {}
export interface User extends Identified, UserData {}

export type IUserSummary = Pick<UserDBO, '_id' | 'email'>

interface Trigger<DateType> {
  triggerDate: DateType
}

export type Target = MsftGraph.Chat | MsftGraph.Channel

export enum DelayedMessageStatus {
  Pending = 'pending',
  Sent = 'sent',
  Deleted = 'deleted',
  Error = 'error',
}

export type TargetType = 'chat' | 'channel' | undefined
export interface Channel extends MsftGraph.Channel {
  teamId: string
}

export interface Attachment {
  id: string
  filename: string
  contentType: string
  size: number
  url: string
  createdAt: string | Date // Can be either Date (server-side) or string (client-side)
  userId?: string // Owner of the attachment
  messageId?: string // Associated message ID (when attached to a message)
}

interface DelayedMessageDataBase<OID> {
  message: string
  targetType: TargetType
  userId: OID
  status: DelayedMessageStatus
  showBanner?: boolean
  removeBranding?: boolean // Premium feature to remove "Sent with Delayed Send" branding
  target: Record<string, any>
  createdMessage?: MsftGraph.ChatMessage
  attachments?: Attachment[] // New field for file attachments
  rrule?: string // iCalendar RRULE string for recurring messages
  originalId?: string // For tracking instances of a recurring message
}
interface DelayedMessageDataToChat<OID> extends DelayedMessageDataBase<OID> {
  targetType: 'chat' | undefined
  target: MsftGraph.Chat
}
interface DelayedMessageDataToChannel<OID> extends DelayedMessageDataBase<OID> {
  targetType: 'channel'
  target: Channel
}

type DelayedMessageData<OID> =
  | DelayedMessageDataToChat<OID>
  | DelayedMessageDataToChannel<OID>

export type DelayedMessageDBO = DBIdentified &
  Trigger<Date> &
  DelayedMessageDataBase<ObjectId>

export type DelayedMessage = Identified & Trigger<string> & DelayedMessageData<string>

export interface AccountDBO extends DBIdentified {
  provider: string
  type: string
  providerAccountId: string
  token_type: string
  scope: string
  expires_at: number
  issued_at?: number
  request_login?: boolean
  ext_expires_in: number
  access_token: string
  id_token: string
  refresh_token?: string
  session_state: string
  userId: ObjectId
}

export interface ODataArray<T> {
  '@odata.context': string
  '@odata.count': number
  '@odata.nextLink': string
  value: T[]
}

export type ODataUnique<T> = T & {
  '@odata.context': string
}

export interface ListParams {
  offset?: number
  limit?: number
}

export type QueryParamsObj = Record<string, string | string[]>

export type MessageCountsByType = Record<DelayedMessageStatus, number>

export interface UserMetrics {
  messages: MessageCountsByType
  request_login: boolean
}

export interface UserWithMetricsDBO extends UserDBO, UserMetrics {}
export interface UserWithMetrics extends User, UserMetrics {}

interface EmailTrackingData {
  templateId: string
  name: string
  userId: ObjectId
}

export interface EmailTrackingDBO extends DBIdentified, EmailTrackingData {}

export enum SubscriptionPlanType {
  Free = 'free',
  Pro = 'pro',
  Team = 'team',
  Gift = 'gift',
}

export enum SubscriptionSource {
  Manual = 'manual',
  Marketplace = 'marketplace',
  TeamAssigned = 'team_assigned',
}

export interface SubscriptionData {
  planType: SubscriptionPlanType
  source: SubscriptionSource
  startDate: Date
  expiryDate: Date
  marketplaceSubscriptionId?: string
  marketplacePlanId?: string
  lastOperationId?: string
  quantity?: number // Used for team subscriptions to store seat count
  features: string[]
}

export interface SubscriptionDBO extends DBIdentified, SubscriptionData {
  accountId: ObjectId
}

export interface SeatAssignment {
  email: string
  accountId?: ObjectId
  assignedDate: Date
}

export interface TeamSubscriptionData extends SubscriptionData {
  name: string
  ownerAccountId: ObjectId
  totalSeats: number
  seatAssignments?: SeatAssignment[]
}

export interface TeamSubscriptionDBO extends DBIdentified, TeamSubscriptionData {
  // Additional properties added via aggregation pipeline (not stored in the database)
  ownerAccount?: any
  ownerUser?: any
  teamOwnerEmail?: string
}

export interface BulkMessageTaskData {
  messageId: string             // Message ID - either user-provided or auto-generated
  title: string
  message: string
  createdAt: Date
  updatedAt: Date
  status: BulkMessageTaskStatus
  imageUrl?: string
  linkUrl?: string
  linkText?: string
  createdBy: string // Admin's email
  filter: {
    emails: string[] | null // null means all users
  }
  progress: {
    total: number
    processed: number
    succeeded: number
    failed: number
    currentBatch: number
    lastProcessedUserId?: string
  }
  error?: string
  processedUserIds?: string[]  // List of user IDs who have already received this message
}

export interface BulkMessageTaskDBO extends DBIdentified, BulkMessageTaskData {}
export interface BulkMessageTask extends Identified, Omit<BulkMessageTaskData, 'createdAt' | 'updatedAt'> {
  createdAt: string
  updatedAt: string
}

export enum MessageType {
  Teams = 'teams',
  Email = 'email',
}

export enum MessageDeliveryStatus {
  Pending = 'pending',
  Sent = 'sent',
  Failed = 'failed',
}

export interface MessageHistoryData {
  messageId: string              // Unique message ID (UUID)
  bulkMessageTaskId?: string     // Reference to bulk task if this was part of a bulk send
  delayedMessageId?: string      // Reference to delayed message if this was a scheduled message
  type: MessageType              // Teams or Email
  userId: ObjectId               // User who received the message
  email: string                  // Email of the recipient
  subject?: string               // Subject (for email) or title (for Teams)
  content: string                // Message content/body
  status: MessageDeliveryStatus  // Delivery status
  sentAt: Date                   // When the message was sent
  receivedAt?: Date              // When the message was confirmed delivered (if available)
  retryCount?: number            // Number of delivery attempts
  errorDetails?: string          // Details if delivery failed
  metadata: Record<string, any>  // Any additional metadata
}

export interface MessageHistoryDBO extends DBIdentified, MessageHistoryData {}

export interface MessageHistory extends Identified, Omit<MessageHistoryData, 'userId' | 'sentAt' | 'receivedAt'> {
  userId: string
  sentAt: string
  receivedAt?: string
}

// Activity log types for tracking user events
export enum ActivityLogType {
  // Message related
  MessageScheduled = 'message_scheduled',
  MessageSent = 'message_sent',
  MessageCancelled = 'message_cancelled',
  MessageEdited = 'message_edited',
  
  // Authentication and account related
  AccountCreated = 'account_created',
  AccountDeleted = 'account_deleted',
  UserLoggedIn = 'user_logged_in',
  UserLoggedOut = 'user_logged_out',
  
  // Subscription related
  SubscriptionCreated = 'subscription_created',
  SubscriptionExpired = 'subscription_expired',
  SubscriptionModified = 'subscription_modified',
  SubscriptionRenewed = 'subscription_renewed',
  SubscriptionRemoved = 'subscription_removed',
  SubscriptionReassignedFrom = 'subscription_reassigned_from',
  SubscriptionReassignedTo = 'subscription_reassigned_to',
  
  // Error related
  MarketplaceTokenError = 'marketplace_token_error',
  PaymentError = 'payment_error',
  APIError = 'api_error',
  AuthenticationError = 'authentication_error',
  
  // Feature usage
  FeatureUsed = 'feature_used',
  SettingsUpdated = 'settings_updated',
  AttachmentUploaded = 'attachment_uploaded',
  EmailSent = 'email_sent',
  BulkMessageSent = 'bulk_message_sent',
}

// Activity log data structure
export interface ActivityLogData {
  userId: ObjectId
  email: string
  type: ActivityLogType
  timestamp: Date
  details: Record<string, any>
}

// Database and client-facing interfaces
export interface ActivityLogDBO extends DBIdentified, ActivityLogData {}
export interface ActivityLog extends Identified, Omit<ActivityLogData, 'userId' | 'timestamp'> {
  userId: string
  timestamp: string
}

// Message Templates
export interface MessageTemplateData {
  name: string
  content: string
  description?: string
  targetType?: TargetType
  isPublic?: boolean // True if template is shared with the team
  createdAt: Date
  updatedAt: Date
  attachments?: Attachment[]
  tags?: string[]
}

export interface MessageTemplateDBO extends DBIdentified, MessageTemplateData {
  userId: ObjectId
}

export interface MessageTemplate extends Identified, Omit<MessageTemplateData, 'createdAt' | 'updatedAt'> {
  userId: string
  createdAt: string
  updatedAt: string
}

// Engagement Messages for Drip Campaign
export interface EngagementMessageData {
  userId: ObjectId
  messageId: string
  sentAt: Date
}

export interface EngagementMessageDBO extends DBIdentified, EngagementMessageData {}